/*! idmp v4.2.3 | (c) github/haozi | MIT */
//#region src/index.ts
var DEFAULT_MAX_AGE = 3e3;
var _7days = 6048e5;
var noop = () => {};
var UNDEFINED = void 0;
var $timeout = setTimeout;
var $clearTimeout = clearTimeout;
/**
* Best-effort timer.unref() so pending TTL flushes don't keep Node CLI
* processes alive. No-op in browsers (setTimeout returns a number, no .unref).
*/
var unrefTimer = (id) => {
	/* istanbul ignore if -- jsdom test env: setTimeout returns number, no .unref */
	if (id && typeof id.unref === "function") try {
		id.unref();
	} catch (_unused) {}
	return id;
};
var getMin = (a, b) => a < b ? a : b;
/**
* Module-level memo of objects that have already been frozen by readonly().
* Re-walking an already-frozen tree on every idmp() call burns time on
* Object.getOwnPropertyDescriptor + try/catch per key — the WeakSet lets
* us bail in O(1) on the hot path. WeakSet so nothing leaks.
*
* Only allocated in dev — the entire readonly path is a no-op in production.
*/
/* istanbul ignore next -- production branch (UNDEFINED) is dead in test env */
var _readonlyDone = process.env.NODE_ENV !== "production" ? /* @__PURE__ */ new WeakSet() : UNDEFINED;
/**
* Makes an object's properties read-only to prevent mutation
*/
var defineReactive = (obj, key, value, visited) => {
	try {
		var _Object$getOwnPropert, _Object$getOwnPropert2;
		readonly(value, visited);
		Object.defineProperty(obj, key, {
			configurable: false,
			enumerable: (_Object$getOwnPropert = (_Object$getOwnPropert2 = Object.getOwnPropertyDescriptor(obj, key)) === null || _Object$getOwnPropert2 === void 0 ? void 0 : _Object$getOwnPropert2.enumerable) !== null && _Object$getOwnPropert !== void 0 ? _Object$getOwnPropert : true,
			get: () => value,
			set: (newValue) => {
				const msg = `[idmp error] The data is read-only, set ${key.toString()}=${JSON.stringify(newValue)} is not allow`;
				console.error(`%c ${msg}`, "font-weight: lighter; color: red", newValue);
				throw new Error(msg);
			}
		});
		return true;
	} catch (_unused2) {
		/* istanbul ignore next */
		return false;
	}
};
/**
* Recursively makes an object and its properties read-only.
* Idempotent: if the object was already processed, returns immediately.
*/
var readonly = (obj, visited) => {
	try {
		if (obj == null || typeof obj !== "object") return obj;
		if (_readonlyDone && _readonlyDone.has(obj)) return obj;
		const protoType = Object.prototype.toString.call(obj);
		if (!["[object Object]", "[object Array]"].includes(protoType)) return obj;
		const isImmerDraft = (obj) => !!obj[Symbol.for("immer-state")];
		if (isImmerDraft(obj)) return obj;
		const proto = Object.getPrototypeOf(obj);
		if (proto !== Object.prototype && proto !== Array.prototype && proto !== null)
 /* istanbul ignore next */
		return obj;
		if (!visited) visited = /* @__PURE__ */ new WeakSet();
		if (visited.has(obj)) return obj;
		visited.add(obj);
		Object.keys(obj).forEach((key) => {
			try {
				const descriptor = Object.getOwnPropertyDescriptor(obj, key);
				if (!descriptor || descriptor.configurable === false) return;
				if (descriptor.get || descriptor.set) return;
				defineReactive(obj, key, obj[key], visited);
			} catch (_unused3) {}
		});
		_readonlyDone && _readonlyDone.add(obj);
		return obj;
	} catch (_unused4) {
		/* istanbul ignore next */
		return obj;
	}
};
/**
* Ensures maxAge is within valid range (0 to 7 days)
* @param maxAge - The requested max age in milliseconds
* @returns A valid max age value
*/
var getRange = (maxAge) => {
	if (maxAge < 0) return 0;
	if (maxAge > _7days) return _7days;
	return maxAge;
};
/**
* Global store for caching promises and their results
* Contains tracking information for each promise identified by globalKey
*/
var _globalStore = {};
/**
* Normalizes options, applying defaults as needed
* @param options - User-provided options
* @returns Normalized options object
*/
var getOptions = (options) => {
	const { maxRetry = 30, maxAge: paramMaxAge = DEFAULT_MAX_AGE, minRetryDelay = 50, maxRetryDelay = 5e3, onBeforeRetry = noop, signal } = options || {};
	return {
		maxRetry,
		maxAge: getRange(paramMaxAge),
		minRetryDelay,
		maxRetryDelay,
		onBeforeRetry,
		f: paramMaxAge === Infinity,
		signal
	};
};
/**
* Clears the cached result for a specific key
* @param globalKey - The key to clear from cache
*/
var flush = (globalKey) => {
	if (!globalKey) return;
	const cache = _globalStore[globalKey];
	if (!cache) return;
	cache[6] && $clearTimeout(cache[6]);
	_globalStore[globalKey] = UNDEFINED;
};
/**
* Clears all cached results
*/
var flushAll = () => {
	for (let key of Object.keys(_globalStore)) flush(key);
	_globalStore = {};
};
/**
* Main IDMP function - Intelligent Deduplication of Multiple Promises
*
* Ensures that multiple calls to the same asynchronous operation (identified by globalKey)
* will reuse the same promise, avoiding duplicate network requests or operations.
* Includes automatic retry logic, caching, and request deduplication.
*
* @param globalKey - Unique identifier for this promise operation
* @param promiseFunc - Function that returns the promise to execute
* @param options - Configuration options
* @returns Promise resolving to the result of promiseFunc
*
* @example
* ```typescript
* // Basic usage
* const data = await idmp('user-123', () => fetchUserData(123));
*
* // With options
* const data = await idmp('user-123', () => fetchUserData(123), {
*   maxRetry: 5,
*   maxAge: 60000, // 1 minute cache
*   onBeforeRetry: (err, {retryCount}) => console.log(`Retry #${retryCount}`)
* });
* ```
*/
var idmp = (globalKey, promiseFunc, options) => {
	if (process.env.NODE_ENV !== "production") options = readonly(options);
	if (!globalKey) return promiseFunc();
	const { maxRetry, minRetryDelay, maxRetryDelay, maxAge, onBeforeRetry, f: isFiniteParamMaxAge, signal } = getOptions(options);
	_globalStore[globalKey] = _globalStore[globalKey] || [
		0,
		0,
		[]
	];
	const cache = _globalStore[globalKey];
	let callStackLocation = "";
	const printLogs = (...msg) => {
		/* istanbul ignore if -- jsdom env always has window */
		if (typeof window === "undefined") return;
		try {
			if (localStorage.idmp_debug === "false") return;
		} catch (_unused5) {}
		/* istanbul ignore else -- Fallback for envs without console.groupCollapsed */
		if (console.groupCollapsed) {
			console.groupCollapsed(...msg);
			console.log("globalKey:", globalKey);
			console.log("callStackLocation:", callStackLocation);
			console.log("data:", cache[3]);
			console.groupEnd();
		} else console.log(...msg);
	};
	/**
	* Resets the promise state for retrying
	*/
	const reset = () => {
		cache[1] = 0;
		cache[3] = cache[4] = UNDEFINED;
	};
	/**
	* Resolves all pending promises with the cached result
	*/
	const doResolves = () => {
		const len = cache[2].length;
		for (let i = 0; i < len; ++i) {
			cache[2][i][0](cache[3]);
			if (process.env.NODE_ENV !== "production") if (i === 0) printLogs(`%c[idmp debug] ${globalKey === null || globalKey === void 0 ? void 0 : globalKey.toString()} from origin`, "font-weight: lighter");
			else printLogs(`%c[idmp debug] ${globalKey === null || globalKey === void 0 ? void 0 : globalKey.toString()} from cache`, "color: gray; font-weight: lighter");
		}
		cache[2] = [];
		if (!isFiniteParamMaxAge) cache[6] = unrefTimer($timeout(() => {
			flush(globalKey);
		}, maxAge));
	};
	/**
	* Rejects all pending promises with the cached error.
	* Retry-spawned dummy promises (added to pendingList by re-entered
	* executePromise calls) have their own .catch(noop) attached at the
	* call site, so it is safe to reject every entry here.
	*/
	const doRejects = () => {
		const list = cache[2];
		cache[2] = [];
		const len = list.length;
		for (let i = 0; i < len; ++i) list[i][1](cache[4]);
		flush(globalKey);
	};
	/**
	* Creates and manages the actual promise execution
	*/
	const executePromise = () => new Promise((resolve, reject) => {
		!cache[5] && (cache[5] = promiseFunc);
		if (process.env.NODE_ENV !== "production") try {
			if (cache[0] === 0 && typeof globalKey !== "symbol") {
				const stack = (/* @__PURE__ */ new Error()).stack || "";
				const baseStack = cache[7];
				if (!baseStack) cache[7] = stack;
				const getCodeLine = (s, offset = 0) => {
					try {
						const arr = s.split("\n").filter((o) => o.includes(":"));
						let idx = Infinity;
						$0: for (const key of [
							"idmp/src/index.ts",
							"idmp/",
							"idmp\\",
							"idmp"
						]) for (let _idx = arr.length - 1; _idx >= 0; --_idx) if (arr[_idx].indexOf(key) > -1) {
							idx = _idx;
							break $0;
						}
						const line = arr[idx + offset + 1] || "";
						if (line.includes("idmp")) return line;
						/* istanbul ignore next */
						return "";
					} catch (_unused6) {
						/* istanbul ignore next */
						return "";
					}
				};
				callStackLocation = getCodeLine(stack, 1).split(" ").pop() || "";
				if (baseStack && baseStack !== stack) {
					const line1 = getCodeLine(baseStack);
					const line2 = getCodeLine(stack);
					if (line1 && line2 && line1 !== line2) console.error(`[idmp warn] the same key \`${globalKey.toString()}\` may be used multiple times in different places\n(It may be a misjudgment and can be ignored):\nsee https://github.com/ha0z1/idmp?tab=readme-ov-file#implementation \n${[
						`1.${line1} ${baseStack}`,
						"------------",
						`2.${line2} ${stack}`
					].join("\n")}`);
				}
			}
		} catch (_unused7) {}
		if (cache[1] === 2) {
			reject(cache[4]);
			return;
		}
		if (cache[1] === 4) {
			/* istanbul ignore else -- production strips printLogs */
			if (process.env.NODE_ENV !== "production") printLogs(`%c[idmp debug] \`${globalKey === null || globalKey === void 0 ? void 0 : globalKey.toString()}\` from cache`, "color: gray;font-weight: lighter");
			resolve(cache[3]);
			return;
		}
		if (signal) {
			if (signal.aborted) {
				cache[1] = 2;
				cache[4] = new DOMException(signal.reason, "AbortError");
				cache[2].push([resolve, reject]);
				doRejects();
				return;
			}
			if (!cache[8]) cache[8] = /* @__PURE__ */ new Set();
			if (!cache[8].has(signal)) {
				cache[8].add(signal);
				signal.addEventListener("abort", () => {
					/* istanbul ignore if -- listener uses { once: true }; same-cache re-entry is unreachable */
					if (cache[1] === 2) return;
					cache[1] = 2;
					cache[4] = new DOMException(signal.reason, "AbortError");
					doRejects();
				}, { once: true });
			}
		}
		if (cache[1] === 0) {
			cache[1] = 1;
			cache[2].push([resolve, reject]);
			cache[5]().then((data) => {
				if (cache[1] === 2) return;
				if (process.env.NODE_ENV !== "production") cache[3] = readonly(data);
				else cache[3] = data;
				doResolves();
				cache[1] = 4;
			}).catch((err) => {
				if (cache[1] === 2) return;
				cache[1] = 3;
				cache[4] = err;
				++cache[0];
				if (cache[0] > maxRetry) doRejects();
				else {
					onBeforeRetry(err, {
						globalKey,
						retryCount: cache[0]
					});
					reset();
					unrefTimer($timeout(() => {
						executePromise().catch(noop);
					}, getMin(maxRetryDelay, minRetryDelay * Math.pow(2, cache[0] - 1))));
				}
			});
		} else if (cache[1] === 1) cache[2].push([resolve, reject]);
	});
	return executePromise();
};
/**
* Clear the cached result for a specific key
*/
idmp.flush = flush;
/**
* Clear all cached results
*/
idmp.flushAll = flushAll;
idmp._s = process.env.NODE_ENV !== "production" ? _globalStore : UNDEFINED;
//#endregion
export { idmp as default, getOptions };

//# sourceMappingURL=index.js.map